import type { PlaceOpeningHours } from '@elara/domain/models/schemas/place-schemas';

import React, { useMemo, useRef, useState } from 'react';

import { extractDatePart } from '@elara/client-core/lib/date-utils';
import { addDurationToDateTime, combineDateAndTime } from '@elara/client-core/lib/itinerary-utils';

import { Checkbox } from './checkbox.js';
import { DateTimeField } from './DateTimeField.js';
import { Label } from './label.js';
import { OpeningHoursTable } from './OpeningHoursTable.js';
import { SectionDivider } from './SectionDivider.js';

// ─── Helpers ────────────────────────────────────────────────────────────────

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(dateStr: string): Date {
  const d = new Date(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function timeStringToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const [hRaw, mRaw] = value.split(':');
  const h = Number.parseInt(hRaw ?? '', 10);
  const m = Number.parseInt(mRaw ?? '', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function minutesToTimeString(totalMinutes: number): string {
  const minutes = Math.max(0, Math.min(totalMinutes, 23 * 60 + 59));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TIME_OPTIONS = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

const MINUTES_PER_DAY = 24 * 60;
const WEEK_MINUTES = 7 * MINUTES_PER_DAY;

interface OpeningRange {
  startMinute: number;
  endMinute: number;
}

function mergeOpeningRanges(ranges: OpeningRange[]): OpeningRange[] {
  if (ranges.length === 0) return [];
  const sorted = ranges.toSorted((a, b) => a.startMinute - b.startMinute);
  const merged: OpeningRange[] = [];
  let current: OpeningRange = { ...sorted[0]! };
  for (let i = 1; i < sorted.length; i++) {
    const range = sorted[i]!;
    if (range.startMinute <= current.endMinute) {
      current.endMinute = Math.max(current.endMinute, range.endMinute);
    } else {
      merged.push(current);
      current = { ...range };
    }
  }
  merged.push(current);
  return merged;
}

function getOpeningRangesForDay(openingHours: PlaceOpeningHours | null | undefined, dateStr: string): OpeningRange[] {
  if (!openingHours?.periods || openingHours.periods.length === 0 || !dateStr) return [];
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return [];
  // dateStr is always YYYY-MM-DD (date-only), which JS parses as UTC midnight.
  // Use getUTCDay() to avoid the browser's local timezone shifting the day.
  const dayIndex = date.getUTCDay();
  const dayStart = dayIndex * MINUTES_PER_DAY;
  const dayEnd = dayStart + MINUTES_PER_DAY;
  const ranges: OpeningRange[] = [];
  for (const period of openingHours.periods) {
    const open = period.open;
    const close = period.close;
    // Google represents "open 24 hours" as open day=0, hour=0, minute=0 with no close field.
    if (open?.day === 0 && open.hour === 0 && open.minute === 0 && close?.day == null) {
      return [{ startMinute: 0, endMinute: MINUTES_PER_DAY }];
    }
    if (
      open?.day == null ||
      open.hour == null ||
      open.minute == null ||
      close?.day == null ||
      close.hour == null ||
      close.minute == null
    )
      continue;
    let openMinutes = open.day * MINUTES_PER_DAY + open.hour * 60 + open.minute;
    let closeMinutes = close.day * MINUTES_PER_DAY + close.hour * 60 + close.minute;
    const crossesWeekBoundary = closeMinutes <= openMinutes;
    if (crossesWeekBoundary) closeMinutes += WEEK_MINUTES;
    const intervals = [{ start: openMinutes, end: closeMinutes }];
    if (crossesWeekBoundary) intervals.push({ start: openMinutes - WEEK_MINUTES, end: closeMinutes - WEEK_MINUTES });
    for (const interval of intervals) {
      const start = Math.max(interval.start, dayStart);
      const end = Math.min(interval.end, dayEnd);
      if (start < end) ranges.push({ startMinute: start - dayStart, endMinute: end - dayStart });
    }
  }
  return mergeOpeningRanges(ranges);
}

function getTimeOptionsForDate(openingHours: PlaceOpeningHours | null | undefined, dateStr: string): string[] {
  if (!openingHours?.periods || openingHours.periods.length === 0 || !dateStr) return TIME_OPTIONS;
  const ranges = getOpeningRangesForDay(openingHours, dateStr);
  if (ranges.length === 0) return [];
  const options: string[] = [];
  for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += 15) {
    if (ranges.some((r) => minutes >= r.startMinute && minutes < r.endMinute)) {
      options.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`);
    }
  }
  return options;
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Google Calendar-style contract: All day shows Start date + End date (an item can span multiple
 * days, no times). Off, it shows Start date, Start time, and End time as three separate field boxes
 * (not Google's own merged Start-date+time box) — product preference, see `DateRangeField`'s render —
 * with NO end date field at all: a timed item is always a same-day block, removing the need to think
 * about an end date for the common case.
 * (An item that genuinely needs to span multiple days AND carry times isn't representable here — the
 * same simplification Google Calendar's own quick-add view makes; a full multi-day-timed editor would
 * be a separate, larger surface.) `selectedEndDate` still exists internally and keeps auto-syncing to
 * the start date in the background while timed (see the Start field's `onDateChange`) purely so it's
 * ready the moment the user switches back to All day.
 */
export interface DateRangeValue {
  startDateTime: string;
  endDateTime: string;
  isValid: boolean;
}

export interface DateRangeFieldProps {
  initialStartDateTime?: string;
  initialEndDateTime?: string;
  tripStartDate?: string;
  tripEndDate?: string;
  /** When provided, filters time options to opening hours and shows the opening hours table. */
  placeOpeningHours?: PlaceOpeningHours | null;
  isOpenNow?: boolean;
  /** Allow selecting dates before today (e.g. recording a past booking). */
  allowPastDates?: boolean;
  /** Called immediately on mount and on every subsequent change. */
  onChange: (value: DateRangeValue) => void;
}

// ─── Initialisation ──────────────────────────────────────────────────────────

interface InitialState {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
}

function parseInitialState(
  initialStartDateTime?: string,
  initialEndDateTime?: string,
  tripStartDate?: string,
  tripEndDate?: string,
): InitialState {
  if (initialStartDateTime) {
    const hasTime = /T\d/.test(initialStartDateTime);
    let startDate = extractDatePart(initialStartDateTime) ?? '';
    if (tripStartDate && startDate && startDate < tripStartDate) startDate = tripStartDate;
    if (tripEndDate && startDate && startDate > tripEndDate) startDate = tripEndDate;
    const startTime = hasTime ? (initialStartDateTime.split('T')[1]?.slice(0, 5) ?? '09:00') : '09:00';

    let endDate = startDate;
    let endTime: string;
    if (initialEndDateTime) {
      const parsedEnd = extractDatePart(initialEndDateTime) ?? startDate;
      // eslint-disable-next-line unicorn/prefer-math-min-max -- string date comparison, not numeric
      endDate = parsedEnd < startDate ? startDate : parsedEnd;
      const endHasTime = /T\d/.test(initialEndDateTime);
      endTime = endHasTime ? (initialEndDateTime.split('T')[1]?.slice(0, 5) ?? '10:00') : '10:00';
    } else {
      try {
        const computed = addDurationToDateTime(initialStartDateTime, 60);
        endTime = computed.split('T')[1]?.slice(0, 5) ?? '10:00';
      } catch {
        endTime = '10:00';
      }
    }

    return { startDate, endDate, startTime, endTime, isAllDay: !hasTime };
  }

  if (tripStartDate) {
    const start = extractDatePart(tripStartDate) || tripStartDate;
    return { startDate: start, endDate: start, startTime: '09:00', endTime: '10:00', isAllDay: false };
  }

  return { startDate: '', endDate: '', startTime: '09:00', endTime: '10:00', isAllDay: false };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DateRangeField({
  initialStartDateTime,
  initialEndDateTime,
  tripStartDate,
  tripEndDate,
  placeOpeningHours,
  isOpenNow,
  allowPastDates = false,
  onChange,
}: DateRangeFieldProps) {
  const [init] = useState(() =>
    parseInitialState(initialStartDateTime, initialEndDateTime, tripStartDate, tripEndDate),
  );

  const [selectedStartDate, setSelectedStartDate] = useState(init.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(init.endDate);
  const [selectedStartTime, setSelectedStartTime] = useState(init.startTime);
  const [selectedEndTime, setSelectedEndTime] = useState(init.endTime);
  const [isAllDay, setIsAllDay] = useState(init.isAllDay);

  // ── Time options ──────────────────────────────────────────────────────────

  const startTimeOptions = useMemo(
    () => (isAllDay ? TIME_OPTIONS : getTimeOptionsForDate(placeOpeningHours ?? null, selectedStartDate)),
    [isAllDay, placeOpeningHours, selectedStartDate],
  );

  // Keyed on `selectedStartDate`, not `selectedEndDate`: a timed item has no end-date field (Google
  // Calendar-style — see the component doc below), so the end time is always considered on the start
  // day. `selectedEndDate` itself still exists and auto-syncs to the start date in the background (see
  // the Start field's `onDateChange`), purely for the All-day date range.
  const endTimeOptions = useMemo(
    () => (isAllDay ? TIME_OPTIONS : getTimeOptionsForDate(placeOpeningHours ?? null, selectedStartDate)),
    [isAllDay, placeOpeningHours, selectedStartDate],
  );

  // ── Time-adjustment effects ───────────────────────────────────────────────

  React.useEffect(() => {
    if (!selectedStartDate || isAllDay) return;
    if (startTimeOptions.length === 0) {
      setSelectedStartTime('');
      return;
    }
    if (!startTimeOptions.includes(selectedStartTime)) setSelectedStartTime(startTimeOptions[0] ?? '');
  }, [selectedStartDate, selectedStartTime, startTimeOptions, isAllDay]);

  // A timed item's end time is always on `selectedStartDate` (no end-date field — see the component
  // doc below), so "later than start" is unconditional here, unlike the old same-day-only check.
  React.useEffect(() => {
    if (!selectedStartDate || isAllDay) return;
    if (endTimeOptions.length === 0) {
      setSelectedEndTime('');
      return;
    }

    const laterOptions = selectedStartTime ? endTimeOptions.filter((opt) => opt > selectedStartTime) : endTimeOptions;

    if (laterOptions.length === 0) {
      setSelectedEndTime('');
      return;
    }

    if (!laterOptions.includes(selectedEndTime)) {
      if (selectedStartTime) {
        const startMinutes = timeStringToMinutes(selectedStartTime);
        if (startMinutes != null) {
          const target = startMinutes + 60;
          const exact = laterOptions.find((opt) => opt === minutesToTimeString(target));
          if (exact) {
            setSelectedEndTime(exact);
            return;
          }
          const nearest = laterOptions.find((opt) => {
            const m = timeStringToMinutes(opt);
            return m != null && m >= target;
          });
          if (nearest) {
            setSelectedEndTime(nearest);
            return;
          }
        }
      }
      setSelectedEndTime(laterOptions[0] ?? '');
    }
  }, [selectedStartDate, selectedStartTime, selectedEndTime, endTimeOptions, isAllDay]);

  // ── Notify parent on every change ────────────────────────────────────────

  const onChangeRef = useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  });

  React.useEffect(() => {
    if (!selectedStartDate || !selectedEndDate) return;
    const startDateTime = isAllDay ? selectedStartDate : combineDateAndTime(selectedStartDate, selectedStartTime);
    // Timed: the end is always on `selectedStartDate`, not `selectedEndDate` — there's no end-date
    // field once timed (see the component doc below).
    const endDateTime = isAllDay ? selectedEndDate : combineDateAndTime(selectedStartDate, selectedEndTime);
    const isValid = isAllDay
      ? selectedEndDate >= selectedStartDate
      : !!selectedStartTime && !!selectedEndTime && endDateTime > startDateTime;
    onChangeRef.current({ startDateTime, endDateTime, isValid });
  }, [selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime, isAllDay]);

  // ── Calendar helpers ──────────────────────────────────────────────────────

  const defaultStartMonth = selectedStartDate
    ? new Date(selectedStartDate)
    : tripStartDate
      ? new Date(tripStartDate)
      : new Date();
  const defaultEndMonth = selectedEndDate
    ? new Date(selectedEndDate)
    : tripEndDate
      ? new Date(tripEndDate)
      : new Date();
  const calendarStartMonth = tripStartDate ? startOfMonth(tripStartDate) : undefined;
  const calendarEndMonth = tripEndDate ? startOfMonth(tripEndDate) : undefined;

  const disabledDay = (day: Date) => {
    const dayStr = toDateString(day);
    if (tripStartDate && dayStr < tripStartDate) return true;
    if (tripEndDate && dayStr > tripEndDate) return true;
    if (!allowPastDates && dayStr < toDateString(new Date())) return true;
    return false;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const onStartDateChange = (dateStr: string) => {
    setSelectedStartDate(dateStr);
    if (!selectedEndDate || dateStr > selectedEndDate) setSelectedEndDate(dateStr);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* No `flex-wrap`: all fields stay on one line, sized by ratio (Start date 50%, each time field
          25%) rather than an equal `flex-1` split — `flex-[2]`/`flex-1` divide the row 2:1:1 regardless
          of container width, instead of a fixed `min-w` that wrapped a 3rd field onto its own line. */}
      <div className="flex items-center gap-4">
        <div className="flex-[2]">
          <DateTimeField
            label={isAllDay ? 'Start date' : 'Date'}
            date={selectedStartDate}
            dateAriaLabel="Start date"
            onDateChange={onStartDateChange}
            showTime={false}
            time=""
            onTimeChange={() => {}}
            timeOptions={[]}
            defaultMonth={defaultStartMonth}
            calendarStartMonth={calendarStartMonth}
            calendarEndMonth={calendarEndMonth}
            disabledDay={disabledDay}
          />
        </div>
        {isAllDay ? (
          <div className="flex-[2]">
            <DateTimeField
              label="End date"
              date={selectedEndDate}
              dateAriaLabel="End date"
              onDateChange={(dateStr) => {
                setSelectedEndDate(dateStr);
                if (!selectedStartDate || dateStr < selectedStartDate) setSelectedStartDate(dateStr);
              }}
              showTime={false}
              time=""
              onTimeChange={() => {}}
              timeOptions={[]}
              defaultMonth={defaultEndMonth}
              calendarStartMonth={calendarStartMonth}
              calendarEndMonth={calendarEndMonth}
              disabledDay={disabledDay}
            />
          </div>
        ) : (
          // Google Calendar-style: no end-date field once timed — a timed item is always a same-day
          // block (see the component doc below) — Start date, Start time, and End time as three
          // distinct fields (not a combined Start date+time box) per product preference.
          <>
            <div className="flex-1">
              <DateTimeField
                label="Start time"
                showDate={false}
                date=""
                onDateChange={() => {}}
                showTime
                time={selectedStartTime}
                onTimeChange={setSelectedStartTime}
                timeOptions={startTimeOptions}
                timeAriaLabel="Start time"
              />
            </div>
            <div className="flex-1">
              <DateTimeField
                label="End time"
                showDate={false}
                date=""
                onDateChange={() => {}}
                showTime
                time={selectedEndTime}
                onTimeChange={setSelectedEndTime}
                timeOptions={endTimeOptions.filter((opt) => !selectedStartTime || opt > selectedStartTime)}
                timeAriaLabel="End time"
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="date-range-all-day" checked={isAllDay} onCheckedChange={(c) => setIsAllDay(c === true)} />
        <Label htmlFor="date-range-all-day" className="text-sm font-normal capitalize cursor-pointer my-0">
          All day
        </Label>
      </div>

      {placeOpeningHours && (
        <div className="space-y-3">
          <SectionDivider label="Opening hours" />
          <div className="text-sm">
            <OpeningHoursTable openingHours={placeOpeningHours} isOpenNow={isOpenNow} />
          </div>
        </div>
      )}
    </div>
  );
}
